import {
  Backdrop,
  Box,
  CircularProgress,
  Container,
  Divider,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import axios from 'axios'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { v4 as uuid4 } from 'uuid'
import { useVerificationGuard } from '../../../hooks/auth/useVerificationGuard'
import { useSnackbar } from '../../../hooks/snackbar/useSnackbar'
import { AppDispatch, RootState } from '../../../store'
import { FormsActions, FormsSelector } from '../../../store/forms'
import { WaterTestFormActions } from '../../../store/waterTestForm'
import { WaterTestParamsActions, WaterTestParamsSelectors } from '../../../store/waterTestParams'
import { WaterTestStripSelector } from '../../../store/waterTestStrip'
import { WaterTestStripActions } from '../../../store/waterTestStrip/waterTestStrip.slice'
import { WaterQualityParameter } from '../../../types/TestParameter'
import { BottomActions, ManualUploadHeader, TestParametersContainer } from './components'
import { ContainerHeader } from './components/uploadManual/ContainerHeader'

export const UploadManual = () => {
  const theme = useTheme()
  const dispatch = useDispatch<AppDispatch>()
  const containerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { showError } = useSnackbar()
  const isVerified = useVerificationGuard('/test-strip/email')
  const formId = 'test-strip-results'
  const formImageId = 'ts_image'
  const [flagParameters, setFlagParameter] = useState<number[] | null>(null)


  const waterTestParameters = useSelector(WaterTestParamsSelectors.makeWaterTestParamsValues)
  //const loading = useSelector(WaterTestStripSelector.loading)
  const loading = useSelector(WaterTestParamsSelectors.isLoading)
  const status = useSelector(WaterTestStripSelector.getWaterTestStripStatus)
  const formValues = useSelector((state: RootState) => FormsSelector.getFormValues(state, formId))
  const imageFormValues = useSelector(FormsSelector.getForm(formImageId))

  const loadParameters = async () => {
    await dispatch(
      WaterTestParamsActions.fetchWaterTestParamsAsync({
        options: {
          params: { $paging: false, $populate: 'analyte_class', $sort:"sort_value asc" },
        },
      }),
    )
  }

  const handleValueSelect = (parameterName: string, value: number) => {
    dispatch(FormsActions.setValue({ id: formId, [parameterName]: value }))
  }
  const handleCancel = () => {
    dispatch(WaterTestFormActions.resetForm())
    navigate('/')
  }

  const handleAnalyze = async () => {
    // if all parameters are not selected, when user clicks on analize, scroll them back to the unselected parameeter and show a marker on the parameter

    const allParametersSelected = waterTestParameters.every((param) =>
      formValues.hasOwnProperty(param.name),
    )
    if (!allParametersSelected) {
      const selectedKeys = Object.keys(formValues)
      const unselectedParameters = waterTestParameters.filter(
        (param) => !selectedKeys.includes(param.name),
      )
      setFlagParameter((unselectedParameters as any).map((item) => item.sort_value))
    } else {
      const selections = waterTestParameters.map((param) => ({
        name: param.name,
        value: formValues[param.name],
        unit: param.measurement || '',
      }))

      try {

        const payload = {
          waterQualityParameters: selections as WaterQualityParameter[],
          isManual: true,
        } as { waterQualityParameters: WaterQualityParameter[]; isManual: boolean; file?: File }
        if (imageFormValues) {
          const request = await axios.get(imageFormValues.localUrl, { responseType: 'blob' })
          const blob = request.data
          const name = `${uuid4()}.${imageFormValues.name.split('.').pop()}`

          payload.file = new File([blob], name, { type: blob.type })
        }
        const response = await dispatch(WaterTestStripActions.manualAnalyzeTestStripAsync(payload))
        if (WaterTestStripActions.manualAnalyzeTestStripAsync.rejected.match(response)) {
          showError('Failed to analyze water test strip')
          return
        }

        if (WaterTestStripActions.manualAnalyzeTestStripAsync.fulfilled.match(response)) {
          dispatch(WaterTestFormActions.resetForm())
          navigate(`/profile`)
        } else {
          showError('Something went wrong')
        }
      } catch (error: any) {
        showError(error || 'Failed to analyze water test strip')
      }
    }
  }

  const scrollTo = () => {
    const parameterElement = document.getElementById(`parameter-${flagParameters![0]}`)

    if (parameterElement) {
      const headerOffset = isMobile ? 120 : 150 // Adjust these values based on your header height
      const elementPosition = parameterElement.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.scrollY - headerOffset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }

  const completedCount = Object.keys(formValues).length
  // const isAnalyzeDisabled = completedCount < waterTestParameters.length
  const isAnalyzeDisabled = completedCount < 1

  useEffect(() => {
    loadParameters()
  }, [])
  useEffect(() => {
    if (flagParameters) {
      handleAnalyze()
      scrollTo()
    } else {
      setFlagParameter(null)
    }
  }, [formValues])

  if (!isVerified) {
    return null
  }

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        maxHeight: 'calc(100vh - 182px)',
        width: '100%',

        display: 'flex',
        flexDirection: 'column',
        //pb: { xs: 16, md: 20 },
        position: 'relative',
      }}
    >
      <Backdrop open={loading} sx={{ zIndex: 1000 }}>
        <CircularProgress size={'7rem'} sx={{ color: theme.palette.primary.main }} />
      </Backdrop>
      <Container
        maxWidth={false}
        ref={containerRef}
        sx={{
          px: { xs: 2, md: 3 },

          overflowY: 'auto',
          flexGrow: 1,
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        <Box
          sx={{
            maxWidth: 800,
            mx: 'auto',
            width: '100%',
            position: 'relative',
          }}
        >
          <ContainerHeader />
          <Stack spacing={{ xs: 3, md: 4 }}>
            <ManualUploadHeader
              selectedValues={formValues}
              parameters={waterTestParameters}
              containerRef={containerRef}
            />

            <Stack
              spacing={3}
              sx={{
                pl: { xs: 0, md: '100px' },
                mt: { xs: 2, md: 3 },
              }}
            >
              <Box sx={{ pl: { xs: 0, md: 1 }, pr: { xs: 0, md: 2 } }}>
                <Divider />
              </Box>
              <TestParametersContainer
                formImageId={formImageId}
                selectedValues={formValues}
                onValueSelect={handleValueSelect}
                flagParameters={flagParameters}
              />
            </Stack>
          </Stack>
        </Box>
      </Container>

      <BottomActions
        currentStep={completedCount}
        totalSteps={waterTestParameters.length}
        onCancel={handleCancel}
        onAnalyze={handleAnalyze}
        isAnalyzeDisabled={isAnalyzeDisabled}
      />
    </Box>
  )
}
